import React, { useEffect, useState, useRef, useCallback } from 'react';

interface ASCIILogoProps {
  width?: number;
  className?: string;
}

const ASCIILogo: React.FC<ASCIILogoProps> = ({ width = 120, className = '' }) => {
  const [asciiArt, setAsciiArt] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);

  // Characters ordered from empty to full
  const ASCII_CHARS = ' .,:;+*?%S#@';

  const generateASCII = useCallback(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `${process.env.PUBLIC_URL}/logo.svg`;

    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const aspectRatio = img.height / img.width;
      const height = Math.floor(width * aspectRatio * 0.5);

      canvas.width = width;
      canvas.height = height;

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const pixels = imageData.data;

      let ascii = '';
      const frame = frameRef.current;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const idx = (y * width + x) * 4;
          const r = pixels[idx];
          const g = pixels[idx + 1];
          const b = pixels[idx + 2];
          const brightness = (r + g + b) / 3;

          // Subtle animated noise based on position and frame
          const noisePhase = Math.sin((x * 0.3) + (y * 0.2) + (frame * 0.1));
          const noise = noisePhase * 15;
          const adjustedBrightness = Math.max(0, Math.min(255, brightness + noise));

          const charIndex = Math.floor((adjustedBrightness / 255) * (ASCII_CHARS.length - 1));
          ascii += ASCII_CHARS[charIndex];
        }
        ascii += '\n';
      }

      setAsciiArt(ascii);
      frameRef.current += 1;
    };
  }, [width]);

  useEffect(() => {
    generateASCII();

    // Subtle animation - regenerate less frequently
    const interval = setInterval(generateASCII, 100);
    return () => clearInterval(interval);
  }, [generateASCII]);

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <pre className={`ascii-logo ${className}`} aria-label="Wetware Logo">
        {asciiArt}
      </pre>
    </>
  );
};

export default ASCIILogo;
